/**
 * No pincode, no cart.
 *
 * The shopper must give a pincode before they can add anything or check out. The
 * point is not to nag — it is that a cart built without one cannot be promised a
 * delivery speed, and the fast-delivery charge cannot be reasoned about at all.
 *
 * WHY A fetch PATCH AND NOT JUST BUTTONS
 *
 * This theme adds to cart from at least four places: the product form, the grid
 * quick-add, the <at-add-to-cart> component and the quick-shop modal — and Buy Now
 * renders inside <shopify-accelerated-checkout>, sometimes in an iframe whose clicks
 * cannot be intercepted at all. Guarding each button means a gate with holes in it,
 * and a new theme section would quietly punch another one.
 *
 * Everything, though, ends at POST /cart/add.js. So that is the gate: one patch on
 * fetch that nothing can route around. The click handlers below are not the gate —
 * they exist so the shopper gets an instant, friendly reason instead of a button
 * that silently does nothing, and so Buy Now can be visibly disabled rather than
 * failing after the fact.
 */

import { EVENT, hasPincode } from './sbr-delivery-core.js'

const BLOCKED_CLASS = 'sbr-gate-blocked'

/* Buy Now cannot be intercepted reliably once clicked, so it is made unclickable
 * instead. The others are intercepted properly and only dimmed for the hint.
 *
 * The magic-* entries are Razorpay Magic Checkout's own buttons. It replaces the
 * theme's Buy Now on the product page and the checkout button in the cart with its
 * own markup, so guarding only the theme's buttons leaves the main purchase path
 * wide open. Class names taken from magic-shopify.js. */
const BUY_SELECTORS = [
  '[data-shopify="payment-button"]',
  'shopify-accelerated-checkout',
  'shopify-accelerated-checkout-cart',
  '.shopify-payment-button',
  '.magic-buy-btn',
  '.magic-buy-btn__parent',
  '.magic-product-clone-btn',
  '[data-magic-branded-btn-ref]',
  '[data-magic-shopify-btn-ref]',
  '[data-magic-native-checkout-shopify-btn-ref]'
]

const ADD_SELECTORS = [
  'button[name="add"]',
  '.add-to-cart',
  '.at-add-to-cart button',
  '.quick-add__button',
  '[data-quick-add]'
]

const CHECKOUT_SELECTORS = [
  'button[name="checkout"]',
  '.cart__checkout',
  '[name="checkout"]',
  '.magic-cart-btn',
  '.magic-cart-btn__parent',
  '.magic-cart-clone-btn',
  '.magic-checkout-btn',
  '.magic-payment-button'
]

let config = { enabled: false, addMessage: '', checkoutMessage: '' }

/* ------------------------------------------------------------------ helpers */

function allowed() {
  return !config.enabled || hasPincode()
}

/** The popup is the one piece of UI that already asks for a pincode well, so the
 *  gate reuses it rather than inventing a second prompt. */
function promptForPincode() {
  const popup = document.querySelector('sbr-delivery-popup')
  if (popup && typeof popup.open === 'function') {
    popup.open()
    return true
  }
  // No popup on the page — fall back to the nearest visible widget.
  const widget = document.querySelector('sbr-delivery [data-sbr-input]')
  if (widget) {
    widget.scrollIntoView({ behavior: 'smooth', block: 'center' })
    widget.focus({ preventScroll: true })
    return true
  }
  return false
}

/** Put the reason next to whatever they clicked, so it is read where they are
 *  looking rather than at the top of the page. */
function showMessage(nearEl, text) {
  if (!text) return
  const host = (nearEl && nearEl.closest('block-buy-buttons, .cart__footer, .cart__item-row, form, sbr-delivery')) ||
    (nearEl && nearEl.parentElement)
  if (!host) return

  let note = host.querySelector(':scope > .sbr-gate-note')
  if (!note) {
    note = document.createElement('p')
    note.className = 'sbr-gate-note'
    note.setAttribute('role', 'alert')
    host.appendChild(note)
  }
  note.textContent = text
  note.classList.add('is-visible')
  clearTimeout(note._t)
  note._t = setTimeout(() => note.classList.remove('is-visible'), 6000)
}

function block(event, el, message) {
  event.preventDefault()
  event.stopPropagation()
  if (event.stopImmediatePropagation) event.stopImmediatePropagation()
  showMessage(el, message)
  promptForPincode()
}

/* ------------------------------------------------- the gate that cannot leak */

/* Razorpay's Buy Now clears the cart and re-adds the item before opening its modal,
 * so the clear is gated too: letting it through while blocking the add would leave
 * the shopper with an empty cart and no idea why. */
const GATED_PATH = /\/cart\/(add|clear)(\.js)?(\?|$)/

function patchFetch() {
  const original = window.fetch
  if (!original || original.__sbrGated) return

  const gated = function (input, init) {
    try {
      const url = typeof input === 'string' ? input : input && input.url
      const method = String((init && init.method) || (input && input.method) || 'GET').toUpperCase()
      const isAdd = method === 'POST' && GATED_PATH.test(String(url || ''))

      if (isAdd && !allowed()) {
        promptForPincode()
        const active = document.activeElement
        showMessage(active && active !== document.body ? active : document.querySelector('.add-to-cart'), config.addMessage)
        // Shaped like a real Shopify cart error so callers that read .description
        // (quick-add does) show something sensible rather than "undefined".
        return Promise.resolve(
          new Response(
            JSON.stringify({ status: 422, message: 'Pincode required', description: config.addMessage }),
            { status: 422, headers: { 'Content-Type': 'application/json' } }
          )
        )
      }
    } catch (e) {
      /* never let the gate break an unrelated request */
    }
    return original.apply(this, arguments)
  }
  gated.__sbrGated = true
  window.fetch = gated
}

/**
 * Razorpay's cart calls go through window.fetch today, so the patch above catches
 * them. It also ships eight XMLHttpRequest call sites, and a future build could move
 * the cart work onto one of them without warning. Covering both transports costs a
 * few lines and removes the need to re-audit a third-party bundle on every release.
 */
function patchXhr() {
  const proto = window.XMLHttpRequest && window.XMLHttpRequest.prototype
  if (!proto || proto.open.__sbrGated) return

  const open = proto.open
  const send = proto.send

  const gatedOpen = function (method, url) {
    this.__sbrBlocked =
      String(method || '').toUpperCase() === 'POST' && GATED_PATH.test(String(url || ''))
    return open.apply(this, arguments)
  }
  gatedOpen.__sbrGated = true
  proto.open = gatedOpen

  proto.send = function () {
    if (this.__sbrBlocked && !allowed()) {
      promptForPincode()
      showMessage(document.querySelector('.add-to-cart'), config.addMessage)
      // Report it as a refusal rather than silently doing nothing, so the caller's
      // error path runs instead of it waiting for a response that never comes.
      Object.defineProperty(this, 'status', { value: 422, configurable: true })
      Object.defineProperty(this, 'readyState', { value: 4, configurable: true })
      Object.defineProperty(this, 'responseText', {
        value: JSON.stringify({ status: 422, description: config.addMessage }),
        configurable: true
      })
      if (typeof this.onreadystatechange === 'function') this.onreadystatechange()
      this.dispatchEvent(new Event('readystatechange'))
      this.dispatchEvent(new Event('load'))
      this.dispatchEvent(new Event('loadend'))
      return
    }
    return send.apply(this, arguments)
  }
}

/* ------------------------------------------------------- visible affordances */

function paint() {
  const ok = allowed()
  BUY_SELECTORS.forEach((sel) =>
    document.querySelectorAll(sel).forEach((el) => el.classList.toggle(BLOCKED_CLASS, !ok))
  )
  ADD_SELECTORS.concat(CHECKOUT_SELECTORS).forEach((sel) =>
    document.querySelectorAll(sel).forEach((el) => el.classList.toggle(BLOCKED_CLASS, !ok))
  )
}

function onClick(event) {
  if (allowed()) return
  const el = event.target && event.target.closest
    ? event.target.closest([...ADD_SELECTORS, ...BUY_SELECTORS, ...CHECKOUT_SELECTORS].join(','))
    : null
  if (!el) return

  const isCheckout = CHECKOUT_SELECTORS.some((s) => el.matches(s))
  block(event, el, isCheckout ? config.checkoutMessage : config.addMessage)
}

function onSubmit(event) {
  if (allowed()) return
  const form = event.target
  if (!form || !form.getAttribute) return
  const action = form.getAttribute('action') || ''

  if (/\/cart\/add/.test(action) || form.dataset.type === 'add-to-cart-form') {
    block(event, form, config.addMessage)
  } else if (/\/cart($|\?)/.test(action) || form.classList.contains('cart__drawer-form')) {
    // The cart form posts to /cart and Shopify reads the clicked button's name, so a
    // checkout press and a quantity update are the same submit. Only the first is gated.
    const pressed = document.activeElement
    if (pressed && pressed.name === 'checkout') block(event, pressed, config.checkoutMessage)
  }
}

/* --------------------------------------------------------------------- init */

function start() {
  const host = document.querySelector('[data-sbr-gate]')
  if (!host) return
  config = {
    enabled: host.dataset.enabled === 'true',
    addMessage: host.dataset.addMessage || 'Please enter your delivery pincode first.',
    checkoutMessage: host.dataset.checkoutMessage || 'Please enter your delivery pincode before checking out.'
  }
  if (!config.enabled) return

  patchFetch()
  patchXhr()
  document.addEventListener('click', onClick, true)
  document.addEventListener('submit', onSubmit, true)
  document.addEventListener(EVENT, paint)

  paint()
  // Quick-add buttons and cart line items are injected after load, so repaint as
  // the DOM changes rather than only once.
  new MutationObserver(() => paint()).observe(document.documentElement, { childList: true, subtree: true })
}

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start)
else start()
